import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, unref as _unref, resolveComponent as _resolveComponent, createVNode as _createVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, withCtx as _withCtx, renderList as _renderList, Fragment as _Fragment, createElementBlock as _createElementBlock, withModifiers as _withModifiers, normalizeClass as _normalizeClass } from "vue"

const _hoisted_1 = { class: "ptop-15 es-right-bar" }
const _hoisted_2 = {
  class: "row ptop-04 title-adjust",
  style: {"padding-bottom":"0.4rem"}
}
const _hoisted_3 = { class: "col w-full" }

import { ref, computed, watch } from 'vue';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import patientapi from '@/api/patientapi';
import { FolderPatientApi, GetSummaryByPatientSummaryTypeEnum, PatientSummaryResponse } from '@/apiGenerate/medserver';
import { medServerApiConfig } from '@/config/medserver-api.config';
import { useAppUserStore } from '@/stores/appUser';
import { useCurrentEntryStore } from '@/stores/currentEntry';
import { useUiStore } from '@/stores/ui';
import { getRootGroupNewIcon, getTextBasedOnLanguage } from '@/functions/expression';
import gen from '@/functions/general';
import { getSingleElement } from '@/utils/array-utils';
import addHistory from './addHistory.vue';
import rBarMoreMenu from './rBarMoreMenu.vue';
import appuserapi from "@/api/appuserapi";

type RootGroupEntry = { rootGroup: string; rootGroupIndex: number; elements: PatientSummaryResponse[]; icon?: string; };
type RootGroupEntryWithoutIndex = Omit<RootGroupEntry, 'rootGroupIndex'>;


export default /*@__PURE__*/_defineComponent({
  __name: 'rBarHistory',
  props: {
    reloadPatientData: { type: Boolean }
  },
  setup(__props: any) {

const props = __props;

const appUserStore = useAppUserStore();
const route = useRoute();
const folderPatientApi = new FolderPatientApi(medServerApiConfig);

const currentEntryStore = useCurrentEntryStore();
const uiStore = useUiStore();
const { t } = useI18n();

const isEditable = computed(() => {
  return currentEntryStore.isEditable && !currentEntryStore.currentEntry.archivedByUser;
});

const patientHistoryResponse = computed(() => {
  return currentEntryStore.patientHistoryResponse;
});
const reloadHistoryData = computed(() => {
  return currentEntryStore.reloadHistoryData;
})

//Drag 'N Drop
const isDraggingOverHistory = ref(false);
function onDragOverHistory() {
  if (currentEntryStore.dndType !== 'exprHistory') {
    isDraggingOverHistory.value = true;
  }
}

function onDragLeaveHistory() {
  isDraggingOverHistory.value = false;
}

async function onDropOnHistory() {
  isDraggingOverHistory.value = false;
  const dndType = currentEntryStore.dndType;
  const dndId = currentEntryStore.dndId;
  currentEntryStore.clearDnd();
  
  if (dndType !== 'exprHistory') {
    await patientapi.setConceptGroupForExpression(dndId, 'History', currentEntryStore.currentEntryId, undefined);
    await loadHistory();
  }
}

function openAddHistory() {
  uiStore.setModalUiId("addHistory");
}

function openHistoryDashBoard(rootGroupFilter: RootGroupEntryWithoutIndex['rootGroup']) {
  currentEntryStore.setHistoryFilter(rootGroupFilter);
  uiStore.setDashBoardType('History' + rootGroupFilter);
}

const  dashbSelect = async(item: PatientSummaryResponse) => {

  const expressions = await folderPatientApi.getExpressionsByConceptGroupAndPatient({
    patientId : currentEntryStore.currentPatientId,
    conceptGroupId: item.conceptGroupId
  });
  currentEntryStore.allExpressions = expressions;
  
  const refSheetSuggestions = await appuserapi.getRefSheetsForExpr(item.expressionId);
  currentEntryStore.setCurrSuggestionsForDashboard(refSheetSuggestions);

  uiStore.setDashBoardItem(item);
}

const historyList = computed(() => {
  if (!currentEntryStore.hasCurrentEntry)       { return []; }

  const sctLanguage = appUserStore.currentAppUserData?.preferences?.sctLanguage;

  // History: get last expressions
  const allHistoryLastExpr = patientHistoryResponse.value.map(expr => gen.deepCopy(expr));

  // Group by site-rootgroups
  let siteRootGroups = allHistoryLastExpr.reduce<Record<string, RootGroupEntry>>((result, expr) => {
    const rootGroup = expr.siteRootGroup;
    
    if (expr.isFamilyHistory === true) return result;
    if (rootGroup === "None") return result;
    if (rootGroup === "Hemato" && expr.aggressorRootGroup === "Substance") return result;                 // Don't show 'calcémie' in 'hémato'
    if (rootGroup === "Hemato" && expr.aggressorRoot === 'SubstanceReaction') return result;              // Don't show 'allergie à la pénicilline' in 'hémato'
    if (expr.sctAggressorProxy === "38341003" || expr.sctAggressorProxy === "392570002") return result;   // Don't show 'HTA proxies' (38341003) in sites ('cardio')
    result[rootGroup] = result[rootGroup] ?? {
      rootGroup,
      rootGroupIndex: expr.siteRootGroupIndex ?? 0,
      elements: []
    };
    result[rootGroup].elements.push(expr);
    return result;
  }, {});

  // Sort by site-rootgroups
  let sortedSiteRootGroups = Object.values(siteRootGroups);
  sortedSiteRootGroups.sort((a, b) => a.rootGroupIndex - b.rootGroupIndex);
  sortedSiteRootGroups.forEach(rootGroup => {
    rootGroup.elements.sort((a, b) => a.siteRootIndex - b.siteRootIndex);
  });

  // Group by aggressor-rootgroups
  let aggressorRootGroups = allHistoryLastExpr.reduce<Record<string, RootGroupEntry>>((result, expr) => {
    if (expr.isFamilyHistory === true) return result;
    const rootGroup = expr.aggressorRootGroup;
    if (rootGroup === "None") return result;
    if (["Procedure", "Dysimmune", "Exogenous"].includes(rootGroup) && expr.siteRoot !== "None") return result;   // Don't show 'cholécystectomie' in procedure if site is present (e.g.'gallbladder')
    if (rootGroup === "Infectious" && expr.siteFindingRoot === "Aggressed") return result;                        // Don't show twice 'pneumonie à pneumocoque'
    if (rootGroup === "Cell" && expr.siteRootGroup === "Hemato") return result;                                   // Don't show twice 'neutropénie' in 'hémato' and 'bio'
    result[rootGroup] = result[rootGroup] ?? {
      rootGroup,
      rootGroupIndex: expr.aggressorRootGroupIndex,
      elements: []
    };
    result[rootGroup].elements.push(expr);
    return result;
  }, {});

  // Sort by aggressor-rootgroups
  let sortedAggressorRootGroups = Object.values(aggressorRootGroups);
  sortedAggressorRootGroups.sort((a, b) => a.rootGroupIndex - b.rootGroupIndex);
  sortedAggressorRootGroups.forEach((rootGroup) => {
    rootGroup.elements.sort((a, b) => a.aggressorRootIndex - b.aggressorRootIndex);
  });

  // Double None rootgroups
  let noneRootGroups = allHistoryLastExpr.reduce<Record<string, RootGroupEntryWithoutIndex>>((result, expr) => {
    if (expr.isFamilyHistory === true) return result;
    const rootGroup = "None";
    if (!(expr.siteRootGroup === "None" && expr.aggressorRootGroup === "None")) return result;
    result[rootGroup] = result[rootGroup] ?? {
      rootGroup,
      elements: []
    };
    result[rootGroup].elements.push(expr);
    return result;
  }, {});
  let sortedNoneRootGroups = Object.values(noneRootGroups);

  // Family
  let familyRootGroups = allHistoryLastExpr.reduce<Record<string, RootGroupEntry>>((result, expr) => {
    if (expr.isFamilyHistory !== true) return result;
    const rootGroup = "FamilyHistory";
    result[rootGroup] = result[rootGroup] ?? {
      rootGroup,
      rootGroupIndex: 0,
      elements: []
    };
    result[rootGroup].elements.push(expr);
    return result;
  }, {});
  
  let sortedFamilyRootGroups = Object.values(familyRootGroups);

  // Merge
  let mergedArray = [
    ...sortedSiteRootGroups, 
    ...sortedAggressorRootGroups, 
    ...sortedNoneRootGroups, 
    ...sortedFamilyRootGroups
  ];

  // Add additional data
  let uiIndex = 0;
  mergedArray.forEach((rootGroup) => {
    rootGroup.icon = getRootGroupNewIcon(rootGroup.rootGroup);
    rootGroup.elements.forEach(expr => expr.text = getTextBasedOnLanguage(expr, sctLanguage));
    rootGroup.elements.sort((a, b) => a.text.localeCompare(b.text));
    rootGroup.elements.forEach(expr => expr.id = gen.newId("rBarHistory", ++uiIndex));
  });
  return mergedArray;
});

async function loadHistory() {
  await folderPatientApi.getSummaryByPatient({ patientId: getSingleElement(route.params.patientId), summaryType: GetSummaryByPatientSummaryTypeEnum.History }).then((response: PatientSummaryResponse[]) => {
    currentEntryStore.setPatientHistoryResponse(response);
  })
}

watch(
  () => props.reloadPatientData,
  async () => {
    if (props.reloadPatientData) {
      await loadHistory();
    }   
  }
);

watch(
  () => reloadHistoryData.value,
  async () => {
    if (reloadHistoryData.value) {
      await loadHistory();
      currentEntryStore.setReloadHistoryData(false);
    }
  }
);


return (_ctx: any,_cache: any) => {
  const _component_es_button = _resolveComponent("es-button")!
  const _component_es_button_icon = _resolveComponent("es-button-icon")!
  const _component_itemModal = _resolveComponent("itemModal")!
  const _component_text_item = _resolveComponent("text-item")!
  const _component_listGroup2 = _resolveComponent("listGroup2")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", {
      onDragover: _withModifiers(onDragOverHistory, ["prevent"]),
      onDragleave: onDragLeaveHistory,
      onDrop: _withModifiers(onDropOnHistory, ["prevent"]),
      class: _normalizeClass([isDraggingOverHistory.value ? 'bigshadow' : ''])
    }, [
      _createElementVNode("div", _hoisted_2, [
        _cache[3] || (_cache[3] = _createElementVNode("div", { class: "mleft-08" }, null, -1)),
        _createVNode(_component_es_button, {
          label: _unref(t)('patient.rightBar.subtitle.history'),
          onClick: _cache[0] || (_cache[0] = ($event: any) => (openHistoryDashBoard(''))),
          "is-primary": false,
          "is-linked": true,
          widthClass: "w-full"
        }, null, 8, ["label"]),
        (_unref(currentEntryStore).dndType !== 'exprHistory' && isEditable.value)
          ? (_openBlock(), _createBlock(_component_es_button_icon, {
              key: 0,
              icon: "plus",
              folder: "shareIcons",
              onClick: _cache[1] || (_cache[1] = ($event: any) => (openAddHistory())),
              styles: "icon-grey-style square-14",
              class: "square-29 justify-center"
            }))
          : _createCommentVNode("", true),
        _createVNode(_component_itemModal, {
          center: true,
          uiId: "addHistory"
        }, {
          modal: _withCtx(() => [
            _createVNode(addHistory, {
              class: "w-full h-full",
              onAdded: _cache[2] || (_cache[2] = ($event: any) => (loadHistory()))
            })
          ]),
          _: 1
        })
      ]),
      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(historyList.value, (rootGroup, rootGroupIndex) => {
        return (_openBlock(), _createElementBlock("div", {
          key: rootGroupIndex,
          class: "row-top dashboard-expression"
        }, [
          _createVNode(_component_es_button_icon, {
            icon: rootGroup.icon,
            folder: "rightBar",
            onClick: ($event: any) => (openHistoryDashBoard(rootGroup.rootGroup)),
            class: "icon-container",
            styles: "icon-grey-style"
          }, null, 8, ["icon", "onClick"]),
          _createElementVNode("div", _hoisted_3, [
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(rootGroup.elements, (expr, exprIndex) => {
              return (_openBlock(), _createBlock(_component_listGroup2, {
                key: exprIndex,
                class: "mleft-04 background full-row",
                onClick: ($event: any) => (dashbSelect(expr))
              }, {
                default: _withCtx(() => [
                  _createVNode(_component_text_item, {
                    class: "pad-s",
                    txt: expr.text,
                    style: {"padding-right":"0"}
                  }, null, 8, ["txt"]),
                  (isEditable.value)
                    ? (_openBlock(), _createBlock(rBarMoreMenu, {
                        key: 0,
                        expr: expr,
                        class: "mleft-auto",
                        refresh: loadHistory,
                        onSetDeletable: ($event: any) => (expr.deletable = false)
                      }, null, 8, ["expr", "onSetDeletable"]))
                    : _createCommentVNode("", true),
                  _cache[4] || (_cache[4] = _createElementVNode("div", { class: "mleft-04" }, null, -1))
                ]),
                _: 2
              }, 1032, ["onClick"]))
            }), 128))
          ])
        ]))
      }), 128))
    ], 34)
  ]))
}
}

})